/* RESPONSIBLE TEAM: team-ai-agent-2 */
import moment from 'moment-timezone';
import type IntlService from 'embercom/services/intl';
import { blocksToText } from './open-ai-prompt';
import type RouterService from '@ember/routing/router-service';
import { KNOWLEDGE_HUB_ENTITIES_NAMES } from './knowledge-hub/constants';
import type AiContentSegment from 'embercom/models/ai-content-segment';
import { htmlToTextContent } from './html-unescape';
import { type Playground } from './fin-playground/playground';
import {
  type PlaygroundQuestion,
  AnswerState,
  CustomerAnswerRating,
  type PlaygroundQuestionSource,
} from './fin-playground/question';
import { type ActionStep } from './fin-playground/action-plan';
import {
  getTestingAsLabel,
  getBrandDropdownLabel,
  shouldShowBrand,
  isBrandDisabled,
} from '../components/ai-agent/playground/settings/helpers';
import type BrandService from 'embercom/services/brand-service';

export default class FinPlaygroundExport {
  app: $TSFixMe;
  intl: IntlService;
  playground: Playground;
  router: RouterService;
  aiContentSegements: Array<AiContentSegment>;
  playgroundApi: any;
  brandService: BrandService;

  constructor(
    app: $TSFixMe,
    intl: IntlService,
    playground: Playground,
    router: RouterService,
    aiContentSegments: Array<AiContentSegment> = [],
    playgroundApi: any,
    brandService: BrandService,
  ) {
    this.app = app;
    this.playground = playground;
    this.intl = intl;
    this.router = router;
    this.aiContentSegements = aiContentSegments;
    this.playgroundApi = playgroundApi;
    this.brandService = brandService;
  }

  private sanitizeHtmlForCsv(text: string): string {
    if (!text) {
      return text;
    }

    // Replace <a> tags that contain href with readable URLs
    text = text.replace(
      /<a[^>]*href\s*=\s*["']([^"']*)["'][^>]*>(.*?)<\/a>/gi,
      (_match, url, linkText) => {
        // If link text is different from URL, show both
        let cleanLinkText = linkText.trim();
        if (cleanLinkText && cleanLinkText !== url) {
          return `${cleanLinkText} (${url})`;
        }
        // Otherwise just show the URL
        return url;
      },
    );

    // Remove <a> tags without href (including their content)
    text = text.replace(/<a(?![^>]*href)[^>]*>.*?<\/a>/gi, '');

    // Remove all other HTML tags
    text = text.replace(/<[^>]*>/g, '');

    return text.trim();
  }

  get fileName() {
    return this.intl.t('ai-agent.playground.download-csv.filename-with-group-name', {
      date: moment.tz(this.app.timezone).format('YYYY-MM-DD'),
      groupName: this.playground.groupName,
    });
  }

  get data() {
    let data = [];

    data.push([
      this.intl.t('ai-agent.playground.download-csv.group-name'),
      this.playground.groupName,
    ]);
    data.push([]);

    data.push(...this.metricsRows());
    data.push([]);

    data.push(...this.settingsRows());
    data.push([]);

    data.push(this.headerRow);
    data.push(...this.playground.filteredQuestions.map((question) => this.row(question)));

    return data;
  }

  row(question: PlaygroundQuestion) {
    let rowArray = [
      question.questionText,
      this.answeredState(question),
      this.answerResponseText(question),
    ];

    rowArray.push(this.automationsUsed(question));

    rowArray.push(this.sources(question), this.personalityApplied(question));

    if (this.app.canUseFinGuidance) {
      rowArray.push(this.guidancesApplied(question));
    }

    rowArray.push(
      this.answerRating(question),
      this.ratingReason(question),
      this.ratingNote(question),
    );

    return rowArray;
  }

  metricsRows() {
    let totalQuestionsCount = [
      this.intl.t('ai-agent.playground.download-csv.stats.total-questions-count'),
      this.playground.countOfFilteredQuestions,
    ];

    let answeredCount = [
      this.intl.t('ai-agent.playground.download-csv.stats.answered-count'),
      this.playground.countOfFilteredQuestionsByAnswerState(AnswerState.Answered),
      `${this.playground.percentageOfFilteredQuestionsByAnswerState(AnswerState.Answered)}%`,
    ];

    let unansweredCount = [
      this.intl.t('ai-agent.playground.download-csv.stats.unanswered-count'),
      this.playground.countOfFilteredQuestionsByAnswerState(AnswerState.Unanswered),
      `${this.playground.percentageOfFilteredQuestionsByAnswerState(AnswerState.Unanswered)}%`,
    ];

    let positiveRatingCount = [
      this.intl.t('ai-agent.playground.download-csv.stats.rated-good-count'),
      this.playground.countOfFilteredQuestionsByAnswerRating(CustomerAnswerRating.Positive),
      `${this.playground.percentageOfFilteredQuestionsByAnswerRating(CustomerAnswerRating.Positive)}%`,
    ];

    let acceptableRatingCount = [
      this.intl.t('ai-agent.playground.download-csv.stats.rated-acceptable-count'),
      this.playground.countOfFilteredQuestionsByAnswerRating(CustomerAnswerRating.Acceptable),
      `${this.playground.percentageOfFilteredQuestionsByAnswerRating(CustomerAnswerRating.Acceptable)}%`,
    ];

    let negativeRatingCount = [
      this.intl.t('ai-agent.playground.download-csv.stats.rated-poor-count'),
      this.playground.countOfFilteredQuestionsByAnswerRating(CustomerAnswerRating.Negative),
      `${this.playground.percentageOfFilteredQuestionsByAnswerRating(CustomerAnswerRating.Negative)}%`,
    ];

    return [
      totalQuestionsCount,
      answeredCount,
      unansweredCount,
      positiveRatingCount,
      acceptableRatingCount,
      negativeRatingCount,
    ];
  }

  settingsRows() {
    let rows = [];
    rows.push([this.intl.t('ai-agent.playground.download-csv.settings.test-settings')]);
    rows.push([
      this.intl.t('ai-agent.playground.download-csv.settings.testing-as'),
      getTestingAsLabel({
        playgroundApi: this.playgroundApi,
        audienceSegments: this.aiContentSegements,
        intl: this.intl,
        shouldTruncate: false,
      }),
    ]);

    // Only show brand in CSV under the same conditions as the modal
    if (
      shouldShowBrand({
        withBrandDropdown: this.brandService.isAppOnRightMultibrandPlan,
        usingAudienceTargeting: this.playground.settings.usingAudienceTargeting,
        isStandaloneApp: this.app.isStandaloneApp,
      })
    ) {
      rows.push([
        this.intl.t('ai-agent.playground.download-csv.settings.brand'),
        getBrandDropdownLabel({
          brandId: this.playground.settings.brandId,
          brands: this.brandService.brands,
          appName: this.app.name,
          intl: this.intl,
          disabled: isBrandDisabled(this.playground.settings),
          defaultBrandId: '0',
        }),
      ]);
    }

    return rows;
  }

  answeredState(question: PlaygroundQuestion) {
    if (question.hasNoAnswer) {
      return this.intl.t('ai-agent.playground.download-csv.answered-state.unanswered');
    }

    if (question.answerHasError) {
      return this.intl.t('ai-agent.playground.download-csv.answered-state.failed');
    }

    return this.intl.t('ai-agent.playground.download-csv.answered-state.answered');
  }

  answerResponseText(question: PlaygroundQuestion) {
    if (question.hasDeletedAutomation) {
      return this.intl.t('ai-agent.playground.download-csv.automations.outdated-answer');
    }

    if (
      question.outcome === 'finished_unresolved' &&
      this.app.canUseFeature('answerbot-playground-disable-human-available')
    ) {
      return this.intl.t('ai-agent.playground.answer.notice.event-log.escalation');
    }

    return this.sanitizeHtmlForCsv(blocksToText(question.responseText));
  }

  automationsUsed(question: PlaygroundQuestion) {
    if (question.hasDeletedAutomation) {
      return this.intl.t('ai-agent.playground.download-csv.automations.outdated-answer');
    }

    if (question.usedCustomAnswer) {
      let route = this.router.urlFor(
        'apps.app.automation.fin-ai-agent.custom-answers.answer.show.index',
        this.app.id,
        question.customAnswer?.id,
      );

      return this.intl.t('ai-agent.playground.download-csv.automations.custom-answer', {
        customAnswerName: question.customAnswer?.title,
        customAnswerLink: `${window.location.origin}${route}`,
      });
    }

    if (question.usedTask) {
      let route = this.router.urlFor(
        'apps.app.automation.fin-ai-agent.tasks.task',
        this.app.id,
        question.triggeredWorkflow?.rulesetId,
      );

      return this.intl.t('ai-agent.playground.download-csv.automations.task', {
        taskName: question.triggeredWorkflow?.title,
        taskLink: `${window.location.origin}${route}`,
      });
    }

    if (!question.actionPlan.hasSteps) {
      return '';
    }

    let data: string[] = [];

    if (question.actionPlan.requiresOtpAuth) {
      data.push(this.intl.t('ai-agent.playground.answer.notice.event-log.action.otp'));
      data.push('');
    }

    if (question.actionPlan.steps.length === 1) {
      let step = question.actionPlan.steps[0];
      data.push(
        this.intl.t('ai-agent.playground.download-csv.automations.action.single', {
          actionName: step.name,
          actionLink: this.actionLink(step),
        }),
      );
    } else {
      data.push(`${this.intl.t('ai-agent.playground.download-csv.automations.action.multiple')}:`);
      data.push('');
      data.push(
        ...question.actionPlan.steps.map((step) => {
          return `- ${step.name} (${this.actionLink(step)})`;
        }),
      );
    }

    return data.join('\n');
  }

  actionLink(step: ActionStep) {
    let route = this.router.urlFor(
      'apps.app.settings.app-settings.custom-actions.custom-action',
      this.app.id,
      step.id,
    );

    return `${window.location.origin}${route}`;
  }

  sources(question: PlaygroundQuestion) {
    let sourcesArray = question.contentSources.map((source) => {
      let entityName = this.entityName(source.entityType);

      return `${source.title} (${this.sourceIntercomUrl(source, entityName)})`;
    });

    return sourcesArray.join('\n');
  }

  sourceIntercomUrl(source: PlaygroundQuestionSource, entityName: string) {
    let route = this.router.urlFor(
      'apps.app.knowledge-hub.view',
      this.app.id,
      entityName,
      source.entityId,
    );

    return `${window.location.origin}${route}`;
  }

  answerRating(question: PlaygroundQuestion) {
    if (question.customerAnswerRating === 'positive') {
      return this.intl.t('ai-agent.playground.download-csv.rating.good');
    } else if (question.customerAnswerRating === 'negative') {
      return this.intl.t('ai-agent.playground.download-csv.rating.poor');
    } else if (question.customerAnswerRating === 'acceptable') {
      return this.intl.t('ai-agent.playground.download-csv.rating.acceptable');
    } else if (question.status === 'completed') {
      return this.intl.t('ai-agent.playground.download-csv.rating.unrated');
    } else {
      return this.intl.t('ai-agent.playground.download-csv.rating.pending');
    }
  }

  ratingReason(question: PlaygroundQuestion) {
    // For positive and acceptable ratings, show the note as the reason
    if (
      question.customerAnswerRating === 'positive' ||
      question.customerAnswerRating === 'acceptable'
    ) {
      return question.customerAnswerRatingNote || '';
    } else if (question.customerAnswerRating === 'negative') {
      let reason = question.customerAnswerRatingReason;

      if (!reason) {
        return '';
      }

      return this.intl.t(`ai-agent.playground.answer-rating-reason.${reason.replace(/_/g, '-')}`);
    }

    return '';
  }

  ratingNote(question: PlaygroundQuestion) {
    // Only show note for negative ratings (Poor)
    // For positive/acceptable ratings, the note is shown as the reason
    if (question.customerAnswerRating === 'negative') {
      return question.customerAnswerRatingNote || '';
    }
    return '';
  }

  personalityApplied(question: PlaygroundQuestion) {
    let toneOfVoiceSettingText = this.intl.t('ai-agent.playground.tone-of-voice.with-label', {
      tone: this.intl.t(`ai-agent.playground.tone-of-voice.${question.aiToneOfVoice}`),
    });
    let answerLengthSettingText = this.intl.t('ai-agent.playground.answer-length.with-label', {
      length: this.intl.t(`ai-agent.playground.answer-length.${question.aiAnswerLength}`),
    });

    return [toneOfVoiceSettingText, answerLengthSettingText].join('\n');
  }

  guidancesApplied(question: PlaygroundQuestion) {
    let guidelines = question.guidelinesWithoutTemplateVars();

    return guidelines.map((guidance) => `"${htmlToTextContent(guidance.text)}"`).join('\n');
  }

  entityName(entityType: keyof typeof KNOWLEDGE_HUB_ENTITIES_NAMES) {
    return KNOWLEDGE_HUB_ENTITIES_NAMES[entityType];
  }

  get headerRow() {
    let rowArray = [
      this.intl.t('ai-agent.playground.download-csv.column-headings.question'),
      this.intl.t('ai-agent.playground.download-csv.column-headings.is-answered'),
      this.intl.t('ai-agent.playground.download-csv.column-headings.response'),
    ];

    rowArray.push(this.intl.t('ai-agent.playground.download-csv.column-headings.automations'));

    rowArray.push(
      this.intl.t('ai-agent.playground.download-csv.column-headings.sources'),
      this.intl.t('ai-agent.playground.download-csv.column-headings.personality-applied'),
      this.intl.t('ai-agent.playground.download-csv.column-headings.guidance-applied'),
      this.intl.t('ai-agent.playground.download-csv.column-headings.response-rating'),
      this.intl.t('ai-agent.playground.download-csv.column-headings.rating-reason'),
      this.intl.t('ai-agent.playground.download-csv.column-headings.rating-note'),
    );

    return rowArray;
  }
}
